import { Typography } from "@mui/material";
import InfoIcon from "components/SVGs/Info";
import { useTranslation } from "react-i18next";
import Button from "components/Button";
import Loader from "components/Loader";
import { useAddStudentByFileMutation } from "app/services/students";
import useToast from "hooks/useToast";
import DropZone, {
  columnsAr,
  columnsEn,
  IStudentRecordWithError,
} from "./DropZone";
import DownloadSample, { sheetColumns } from "./DownloadSample";
import { useContext, useEffect, useState } from "react";
import { includesTwice, StudentFieldType, validate } from "../utils/validate";
import { IValidationErrors } from "features/common/Interfaces";
import { IStudent } from "features/Students";
import { classesLoaded, IClass, selectClasses } from "features/Classes";
import { IFile, IUploadedRow } from "../../Content/useFileBuilder";
import { FireBaseContext } from "contexts/FireBase";
import { MasterDataContext } from "contexts/MasterDataContext";
import { useFetchClassesQuery } from "app/services/classes";
import { useAppDispatch, useAppSelector } from "app/hooks";

const customStyles = {
  root: {
    direction: "rtl",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "580px",
    padding: "20px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "17px",
    overflowY: "auto",
    overflowX: "hidden",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  note: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    paddingBottom: "20px",
  },
};

interface IProps {
  onSubmit(): void;
  classDetails: IClass | null;
}

const UploadFile = ({ onSubmit, classDetails }: IProps) => {
  const dispatch = useAppDispatch();
  const { logEvent } = useContext(FireBaseContext);
  const [validData, setValidData] = useState<IStudentRecordWithError[]>();
  const [errorData, setErrorData] = useState<IStudentRecordWithError[]>();
  const classList = useAppSelector(selectClasses);
  const toast = useToast();
  const { t, i18n } = useTranslation();
  const [addStudent, { isLoading }] = useAddStudentByFileMutation();
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const { data: classesData } = useFetchClassesQuery(schoolDetails?.schoolId, {
    skip: !schoolDetails?.schoolId,
  });

  useEffect(() => {
    if (classesData?._statusCode === 200) {
      const list = classesData._entity?.map((item) => {
        const classInfo: IClass = {
          grade: item._grade,
          teacher: item._teacherName,
          id: item._id,
          name: item._name,
          teacherId: item._teacherId,
          gradeId: item._gradeId,
          students: item._noOfStudents,
        };
        return {
          ...classInfo,
        };
      });
      dispatch(classesLoaded(list ?? []));
    }
  }, [classesData]);

  const createHandler = async () => {
    if (validData?.length)
      try {
        const recordsInfo: IStudentRecordWithError[] =
          structuredClone(validData);
        const studentsInfo: Partial<IStudent>[] = recordsInfo.map((rec) => ({
          name: rec.name,
          studentId: rec.studentId,
          classId:
            classList.find(
              (c) =>
                c.name.toLocaleLowerCase() ===
                rec.className?.toLocaleLowerCase()
            )?.id ?? classDetails?.id,
          email: rec.email,
        }));
        const data = await addStudent(studentsInfo).unwrap();
        if ([200, 624].includes(data._statusCode)) {
          const invalidRecords: IStudentRecordWithError[] = data._entity?.length
            ? data._entity.map((rec) => ({
                name: rec._fullName,
                email: rec._emailAddress,
                studentId: rec._studentSchoolUid,
                className: classList.find(
                  (c) => c.id.toString() === rec._classId.toString()
                )?.name,
                errors: JSON.stringify({
                  [t(sheetColumns[0])]: t("studentId already exists"),
                }),
              }))
            : [];
          const updateErrorData = [
            ...structuredClone(errorData ?? []),
            ...invalidRecords,
          ];
          setErrorData(updateErrorData);
          if (!updateErrorData?.length) {
            toast({
              label: t("success"),
              message: t("Students Added"),
              rtl: i18n.dir() === "rtl",
              type: "success",
              toastId: "studentsAdd",
            });
            onSubmit();
          } else
            toast({
              label: t("Note"),
              message: t("Some Students not Added"),
              rtl: i18n.dir() === "rtl",
              type: "success",
              toastId: "studentsAdd",
            });
          setValidData(undefined);
        } else throw data;
      } catch (error) {
        toast({
          label: t("error"),
          message: t("Something went wrong"),
          rtl: i18n.dir() === "rtl",
          type: "error",
          toastId: "studentsAdd",
        });
        console.log(error);
      }
    logEvent("ClickOnCreateStudent", { Creation_Type: "Excel bulk" });
  };

  const checkDuplicatedIds = (records: IStudentRecordWithError[]) => {
    // the case if studentId duplicated, traversal all records to add this error
    const updatedRecords = structuredClone(records);
    const ids = updatedRecords.map((record) => record.studentId);
    let duplicatedKeys: number[] = [];
    ids.forEach((id) => {
      if (id) {
        const { count, indexes } = includesTwice(ids, id);
        if (count > 1)
          duplicatedKeys = Array.from(
            new Set<number>([...duplicatedKeys, ...indexes])
          );
      }
    });

    duplicatedKeys.forEach((i) => {
      updatedRecords[i].errors = JSON.stringify({
        [t(sheetColumns[0])]: t("duplicate studentId"),
        ...(JSON.parse(updatedRecords[i].errors ?? "{}") as IValidationErrors),
      });
    });

    return updatedRecords;
  };
  const validateUploadedData = (file: IFile, language: "ar" | "en") => {
    if (file?.data?.length === 0) {
      setValidData(undefined);
      setErrorData(undefined);
    }
    const updateValidData: IStudentRecordWithError[] = [];
    const invalidData: IStudentRecordWithError[] = [];
    const uploadedFile = structuredClone(file);
    const columns = language === "ar" ? columnsAr : columnsEn;
    uploadedFile.data.forEach((row) => {
      let {
        [columns[0]]: studentId,
        [columns[1]]: name,
        [columns[2]]: email,
        [columns[3]]: className,
      } = row as IUploadedRow;
      if (name || studentId || email || className) {
        const rowData: IStudentRecordWithError = {
          studentId: studentId?.toString()?.trim(),
          name: name?.toString()?.trim(),
          email: email?.toString()?.trim(),
          className: className?.toString()?.trim(),
        };

        const errors = validate(rowData, {});

        const classNames = classList.map((c) => c.name);
        if (!rowData.className) {
          errors["className"] = "Class Name is required";
        } else if (!classNames.includes(rowData.className)) {
          errors["className"] = "Incorrect Class name";
        }

        if (!rowData.name) {
          errors["name"] = "Student Name is required";
        }

        if (Object.keys(errors).length !== 0) {
          const translateErrors: IValidationErrors = {};
          const sheetColumnsNames = {
            studentId: sheetColumns[0],
            name: sheetColumns[1],
            email: sheetColumns[2],
            className: sheetColumns[3],
          };
          for (const error in errors) {
            translateErrors[
              t(sheetColumnsNames[error as StudentFieldType | "className"])
            ] = t(errors[error]);
          }
          rowData.errors = JSON.stringify(translateErrors);
          invalidData.push(rowData);
        } else {
          updateValidData.push(rowData);
        }
      }
    });

    const falsyData = checkDuplicatedIds(invalidData);
    const records = checkDuplicatedIds(updateValidData);
    const correctData: IStudentRecordWithError[] = [];

    records.forEach((record) => {
      if (record.errors?.length) falsyData.push(record);
      else correctData.push(record);
    });

    if (correctData.length === 0) {
      toast({
        label: t("error"),
        message: t("Adding students failed"),
        rtl: i18n.dir() === "rtl",
        type: "error",
        toastId: "Uploads-has-failed",
      });
    }
    setValidData(correctData);
    setErrorData(falsyData);
  };
  return (
    <>
      <Loader open={isLoading} />
      <Typography
        component="div"
        sx={{ ...customStyles.root, direction: i18n.dir() }}
        tabIndex={-1}
      >
        <Typography component="div" sx={customStyles.note}>
          <InfoIcon />
          <Typography sx={{ color: "rgb(105 123 140)", fontSize: "16px" }}>
            {t("Download File to Upload students")}
          </Typography>
        </Typography>
        <DownloadSample />
        <DropZone
          errorData={errorData}
          classList={classList}
          showError={!validData?.length}
          validateUploadedData={validateUploadedData}
        />
        <Typography
          component="div"
          sx={{ display: "flex", flexDirection: "row", width: "100%" }}
        >
          <Button
            label={t("Create students")}
            action={createHandler}
            styles={{ width: "125px", fontSize: "14px" }}
            disable={!validData?.length}
          />
        </Typography>
      </Typography>
    </>
  );
};

export default UploadFile;
